import { toBigInt } from '@/utils/bigNumber'
import { ActionConfig, ActionContext } from '../../../logic/types'
import { ensureConfigTypes } from '@/domain/hooks/useWrite'
import { VaultWithdrawAction } from '../types'
import { ATOMIC_QUEUE_VAULT_ABI } from '@/config/abis/vaults/atomicQueueVaultAbi'
import { DEFAULT_SLIPPAGE } from '@/config/consts'
import { VaultType } from '@/features/usdxl/types/vaults'
import { HYPERBEAT_VAULT_ABI } from '@/config/abis/vaults/hyperbeatVaultAbi'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { createNativeWithdrawBatchConfig } from '@/domain/evc/transactionFactory'
import {
  getEulerPortfolioInvalidationKeys,
  getPortfolioBalanceInvalidationKeys,
} from '@/features/actions/logic/invalidation'

export function createVaultWithdrawActionConfig(action: VaultWithdrawAction, context: ActionContext): ActionConfig {
  if (action.vaultType === VaultType.NUCLEUS) {
    return vaultNucleusDepositActionConfig(action, context)
  }

  if (action.vaultType === VaultType.EEARN) {
    return vaultEEarnWithdrawActionConfig(action, context)
  }

  return vaultHyperbeatDepositActionConfig(action, context)
}

function vaultHyperbeatDepositActionConfig(action: VaultWithdrawAction, context: ActionContext): ActionConfig {
  const { account, chainId } = context

  const withdrawAmount = toBigInt(action.token.toBaseUnit(action.value))

  return {
    getWriteConfig: () => {
      return ensureConfigTypes({
        abi: HYPERBEAT_VAULT_ABI,
        address: action.vaultAddress,
        functionName: 'requestRedeem',
        chainId,
        args: [withdrawAmount, account, account],
      })
    },

    invalidates: () => [...getEulerPortfolioInvalidationKeys({ account, chainId }), ['vault-withdrawal-events']],
  }
}

function vaultEEarnWithdrawActionConfig(action: VaultWithdrawAction, context: ActionContext): ActionConfig {
  const { account, chainId } = context

  const withdrawAmount = toBigInt(action.token.toBaseUnit(action.value))
  // For max withdrawals, use the exact share balance to avoid dust
  const redeemShares = action.isMax && action.userShares ? BigInt(action.userShares) : withdrawAmount

  return {
    getWriteConfig: () => {
      // If unwrapping to native HYPE, use EVC batch via wagmi-evc's WETHTransactionFactory
      // This handles: withdraw to EVC → unwrap WHYPE → send native HYPE
      // If isMax, also redeems any dust shares at the end
      if (action.unwrapToNative) {
        const configData = createNativeWithdrawBatchConfig({
          vault: action.vaultAddress,
          amount: action.isMax && action.userShares ? BigInt(action.userShares) : withdrawAmount,
          account,
          receiver: account,
          chainId,
          isMax: action.isMax,
        })

        return ensureConfigTypes(configData)
      }

      // Standard ERC20 redeem - use shares for max to avoid dust
      return ensureConfigTypes({
        abi: eEarnVaultImplementationAbi,
        address: action.vaultAddress,
        functionName: 'redeem',
        chainId,
        args: [redeemShares, account, account],
      })
    },

    invalidates: () => getEulerPortfolioInvalidationKeys({ account, chainId }),
  }
}

function vaultNucleusDepositActionConfig(action: VaultWithdrawAction, context: ActionContext): ActionConfig {
  const { account, chainId } = context

  const withdrawAmount = toBigInt(action.token.toBaseUnit(action.value))

  const atomicPrice = (BigInt(action.rateInQuoteSafeWithFee) * BigInt(10000 * (1 - DEFAULT_SLIPPAGE))) / 10000n

  return {
    getWriteConfig: () => {
      return ensureConfigTypes({
        abi: ATOMIC_QUEUE_VAULT_ABI,
        address: action.atomicQueueVaultAddress,
        functionName: 'updateAtomicRequest',
        chainId,
        args: [
          action.vaultAddress,
          action.depositToken.address,
          {
            deadline: BigInt(Math.floor(action.deadline / 1000)),
            atomicPrice,
            offerAmount: withdrawAmount,
            inSolve: false,
          },
        ],
      })
    },

    invalidates: () => [...getPortfolioBalanceInvalidationKeys({ account, chainId }), ['vault-withdrawal-events']],
  }
}
